import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { DepartmentLocation } from "../../utils/models";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { useLocationParCounts } from "../../utils/warningDialogs/useLocationParCounts";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import type { DropdownItem } from "../UIToolkit/Inputs/SystemDropDown/types";
import { buildLocationItems, recentCount } from "./locationPickerItems";
import { NURSE_LOCATION_HISTORY_WINDOW_DAYS, useNurseLocationHistory } from "./useNurseLocationHistory";

/** Metadata about the option that was just committed, so a caller can enrich its own analytics event. */
export type LocationPickerSelectionMeta = { location: string; hasPar: boolean; belowPar: boolean; recentShifts: number; isCurrent: boolean };

type LocationPickerProps = {
	locations: DepartmentLocation[];
	selectedLocation: string;
	onChange: (location: string, meta: LocationPickerSelectionMeta) => void;
	/** When set, the picker fetches this nurse's recent-shift history and shows subtitles + "works most" grouping. */
	nurseId?: number | null;
	/** Drives the "Where {name} works most" section header. */
	nurseFirstName?: string;
	/** The location the staffer is currently on — pinned first with a "Current" badge, even at 0 recent / no par. */
	currentLocation?: string;
	weekIndex: number;
	dayNo: number;
	shiftIndex: number;
	isActual: boolean;
	disabled?: boolean;
	clearable?: boolean;
	label?: string;
	/** Fired when the picker's panel closes (pick or dismiss) — lets an inline-edit host exit edit mode. */
	onClose?: () => void;
	"data-testid"?: string;
};

/**
 * The one location picker used by every "assign a nurse to a location" surface (day view, add nurse, approve
 * request). Composes the generic `SystemDropdown` primitive — it does NOT fork it — and layers the scheduling
 * domain on top: a "N recent shifts" subtitle, a "Current" badge on the staffer's present location, and a
 * par pill (`current/target`, red when below par, muted "no par" when the location has no configured target).
 *
 * Ordering: the current location is pinned first (always shown); the rest are grouped into "works most"
 * (recent shifts > 0) and "other locations", each ranked below-par first, then most-worked, then by name.
 * With no `nurseId` it degrades to a flat, unsubtitled list in the department's location order — but still
 * renders par pills.
 */
export const LocationPicker = ({
	locations,
	selectedLocation,
	onChange,
	nurseId,
	nurseFirstName,
	currentLocation,
	weekIndex,
	dayNo,
	shiftIndex,
	isActual,
	disabled,
	clearable,
	label,
	onClose,
	"data-testid": dataTestId,
}: LocationPickerProps) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const getParCounts = useLocationParCounts();

	const historyQuery = useNurseLocationHistory(chosenDepartment.department_id, nurseId, NURSE_LOCATION_HISTORY_WINDOW_DAYS);
	const counts = historyQuery.data?.countsByLocation;

	const parCounts = useMemo(() => getParCounts({ weekIndex, dayNo, shiftIndex, isActual }), [getParCounts, weekIndex, dayNo, shiftIndex, isActual]);
	const uniqueNames = useMemo(() => getUniqueLocations(locations, false).map((loc) => loc.location), [locations]);

	const items = useMemo<DropdownItem<string>[]>(
		() => buildLocationItems(uniqueNames, nurseFirstName, { counts, parCounts, nurseId, currentLocation, t, dataTestId }),
		[uniqueNames, nurseFirstName, counts, parCounts, nurseId, currentLocation, t, dataTestId],
	);

	// A department with no locations renders nothing, matching every current caller's guard.
	if (uniqueNames.length === 0 && !currentLocation) return null;

	const metaFor = (name: string): LocationPickerSelectionMeta => {
		const par = parCounts[name];
		return {
			location: name,
			hasPar: !!par?.hasPar,
			belowPar: !!par?.belowPar,
			recentShifts: recentCount(name, nurseId, counts),
			isCurrent: !!currentLocation && name === currentLocation,
		};
	};

	return (
		<SystemDropdown<string>
			data-testid={dataTestId}
			label={label}
			items={items}
			selectedItem={selectedLocation}
			onChange={(value) => onChange(value ?? "", metaFor(value ?? ""))}
			clearable={clearable}
			disabled={disabled}
			loading={historyQuery.isLoading}
			fullWidth
			onClose={onClose}
			search={{ placeholder: t("locationPicker.filterPlaceholder") }}
		/>
	);
};
